export const SQUAD_COMMITMENT_TYPES = [
  "in",
  "maybe",
  "rest_day",
  "working",
  "pub_later",
  "first_lift",
  "dawn_patrol",
  "first_lap",
  "first_launch",
  "meet_here",
] as const;

export const SQUAD_STATUS_TYPES = [
  "out_now",
  "at_lunch",
  "pub",
  "heading_home",
  "meet_here",
  "watching_conditions",
  "rest_day",
] as const;

export const SQUAD_MEETUP_LOCATION_TYPES = [
  "venue",
  "lift",
  "beach",
  "resort_point",
  "free_pin",
  "saved_place",
] as const;

export type SquadCommitmentType = (typeof SQUAD_COMMITMENT_TYPES)[number];
export type SquadStatusType = (typeof SQUAD_STATUS_TYPES)[number];
export type SquadMeetupLocationType =
  (typeof SQUAD_MEETUP_LOCATION_TYPES)[number];
export type SquadMeetupRsvpState = "in" | "maybe" | "cant_make_it";
export type CompanionPeopleLoopStatus =
  | "ready"
  | "empty"
  | "partial"
  | "unavailable"
  | "error";

export type CompanionPeopleSquad = {
  id: string;
  name: string;
  iconKey: string;
  memberCount: number;
  notificationsMuted: boolean;
  shareRollCall: boolean;
  shareStatus: boolean;
  shareLeaderboards: boolean;
  socialEnabled: boolean;
  socialPushConsent: boolean;
  socialEmailConsent: boolean;
};

export type CompanionRollCallEntry = {
  id: string;
  squadId: string;
  squadName: string;
  userId: string;
  displayName: string;
  targetLocalDate: string;
  timezone: string;
  baseId: string | null;
  placeSlug: string | null;
  placeName: string;
  sport: string | null;
  commitmentType: SquadCommitmentType;
  aimTimeLocal: string | null;
  meetupId: string | null;
  note: string | null;
};

export type CompanionMeetup = {
  id: string;
  squadId: string;
  squadName: string;
  creatorUserId: string;
  creatorDisplayName: string;
  targetLocalDate: string;
  timezone: string;
  timeLocal: string | null;
  label: string;
  locationType: SquadMeetupLocationType;
  placeSlug: string | null;
  placeName: string | null;
  note: string | null;
  expiresAt: string;
  viewerRsvp: SquadMeetupRsvpState | null;
  rsvpCounts: Record<SquadMeetupRsvpState, number>;
};

export type CompanionStatusPin = {
  id: string;
  squadId: string;
  squadName: string;
  userId: string;
  displayName: string;
  statusType: SquadStatusType;
  targetLocalDate: string;
  timezone: string;
  placeSlug: string | null;
  placeName: string;
  approximateLabel: string | null;
  meetupId: string | null;
  expiresAt: string;
};

export type CompanionSocialMoment = {
  id: string;
  squadId: string;
  squadName: string;
  actorDisplayName: string;
  momentType: string;
  occurredAt: string;
  targetLocalDate: string | null;
  timezone: string;
  sport: string | null;
  placeName: string | null;
  summary: string;
  rankBucket: "high" | "normal" | "recap" | "quiet";
  steezeCount: number;
  viewerGaveSteeze: boolean;
  steezeDisplayNames: string[];
};

export type CompanionPeopleLoopData = {
  status: CompanionPeopleLoopStatus;
  squads: CompanionPeopleSquad[];
  rollCalls: CompanionRollCallEntry[];
  meetups: CompanionMeetup[];
  statusPins: CompanionStatusPin[];
  moments: CompanionSocialMoment[];
  unavailableSources: Array<"roll-call" | "meetups" | "statuses" | "moments">;
  demoLeaderboardQualifyingMembers?: number;
};

export type SquadRollCallInput = {
  squadId: string;
  targetLocalDate: string;
  timezone: string;
  baseId: string | null;
  placeSlug: string | null;
  placeName: string | null;
  sport: string | null;
  commitmentType: string;
  aimTimeLocal: string | null;
  meetupId: string | null;
  note: string | null;
};

export type NormalisedSquadRollCallInput = Omit<
  SquadRollCallInput,
  "commitmentType" | "placeName" | "placeSlug" | "sport" | "note"
> & {
  commitmentType: SquadCommitmentType;
  placeName: string;
  placeSlug: string | null;
  sport: string | null;
  note: string | null;
};

export type SquadMeetupInput = {
  squadId: string;
  targetLocalDate: string;
  timezone: string;
  timeLocal: string | null;
  label: string;
  locationType: string;
  placeSlug: string | null;
  placeName: string | null;
  latitude: number | null;
  longitude: number | null;
  note: string | null;
};

export type NormalisedSquadMeetupInput = Omit<
  SquadMeetupInput,
  "locationType" | "label" | "placeSlug" | "placeName" | "note"
> & {
  locationType: SquadMeetupLocationType;
  label: string;
  placeSlug: string | null;
  placeName: string | null;
  note: string | null;
  expiresAt: string;
};

export type SquadStatusInput = {
  squadId: string;
  statusType: string;
  targetLocalDate: string;
  timezone: string;
  placeSlug: string | null;
  placeName: string | null;
  approximateLabel: string | null;
  meetupId: string | null;
};

export type NormalisedSquadStatusInput = Omit<
  SquadStatusInput,
  "statusType" | "placeSlug" | "placeName" | "approximateLabel"
> & {
  statusType: SquadStatusType;
  placeSlug: string | null;
  placeName: string;
  approximateLabel: string | null;
};

type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,95}$/;
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const SPORT_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function defaultRollCallDate(
  now: Date,
  timezone: string,
): { date: string | null; label: "Today" | "Tomorrow" | null } {
  const local = localParts(now, timezone);
  if (!local) return { date: null, label: null };
  if (local.hour < 16) return { date: local.date, label: "Today" };
  return { date: shiftDateId(local.date, 1), label: "Tomorrow" };
}

export function normaliseRollCallInput(
  input: SquadRollCallInput,
  options: { now?: Date } = {},
): ParseResult<NormalisedSquadRollCallInput> {
  const now = options.now ?? new Date();
  const allowedDates = allowedLocalDates(now, input.timezone);
  const placeName = normaliseText(input.placeName, 120);
  const placeSlug = normaliseSlug(input.placeSlug);
  const baseId = normaliseUuid(input.baseId);

  if (!normaliseUuid(input.squadId) || !placeName || (!placeSlug && !baseId)) {
    return {
      ok: false,
      error: "Choose a Squad and place before joining the roll call.",
    };
  }
  if (!allowedDates || !allowedDates.has(input.targetLocalDate)) {
    return { ok: false, error: "Choose Today or Tomorrow for the roll call." };
  }
  if (!isCommitmentType(input.commitmentType)) {
    return { ok: false, error: "Choose a roll-call plan." };
  }

  const aimTimeLocal = normaliseTime(input.aimTimeLocal);
  if (input.aimTimeLocal && !aimTimeLocal) {
    return { ok: false, error: "Choose a valid target time." };
  }
  const note = normaliseText(input.note, 120);
  if (input.note?.trim() && !note) {
    return { ok: false, error: "Keep the roll-call note to 120 characters." };
  }
  const meetupId = normaliseUuid(input.meetupId);
  if (input.meetupId && !meetupId) {
    return { ok: false, error: "Choose a valid meet-up." };
  }
  const sport = normaliseSport(input.sport);
  if (input.sport && !sport) {
    return { ok: false, error: "Choose a valid sport." };
  }

  return {
    ok: true,
    value: {
      squadId: input.squadId,
      targetLocalDate: input.targetLocalDate,
      timezone: input.timezone,
      baseId,
      placeSlug,
      placeName,
      sport,
      commitmentType: input.commitmentType,
      aimTimeLocal,
      meetupId,
      note,
    },
  };
}

export function normaliseMeetupInput(
  input: SquadMeetupInput,
  options: { now?: Date } = {},
): ParseResult<NormalisedSquadMeetupInput> {
  const allowedDates = allowedLocalDates(options.now ?? new Date(), input.timezone);
  const label = normaliseText(input.label, 80);
  const note = normaliseText(input.note, 160);
  const timeLocal = normaliseTime(input.timeLocal);
  const locationType = isMeetupLocationType(input.locationType)
    ? input.locationType
    : null;

  if (!normaliseUuid(input.squadId) || !allowedDates?.has(input.targetLocalDate)) {
    return { ok: false, error: "Choose a valid Squad and day for the meet-up." };
  }
  if (!label || !locationType || (input.timeLocal && !timeLocal)) {
    return { ok: false, error: "Add a valid meet-up label, place and time." };
  }
  if (input.note?.trim() && !note) {
    return { ok: false, error: "Keep the meet-up note to 160 characters." };
  }
  if (
    locationType === "free_pin" &&
    (!validLatitude(input.latitude) || !validLongitude(input.longitude))
  ) {
    return { ok: false, error: "Choose a valid meet-up point." };
  }

  const expiresAt = localDayEnd(input.targetLocalDate, input.timezone);
  if (!expiresAt) return { ok: false, error: "Choose a valid meet-up timezone." };

  return {
    ok: true,
    value: {
      squadId: input.squadId,
      targetLocalDate: input.targetLocalDate,
      timezone: input.timezone,
      timeLocal,
      label,
      locationType,
      placeSlug: normaliseSlug(input.placeSlug),
      placeName: normaliseText(input.placeName, 120),
      latitude: locationType === "free_pin" ? input.latitude : null,
      longitude: locationType === "free_pin" ? input.longitude : null,
      note,
      expiresAt,
    },
  };
}

export function normaliseStatusInput(
  input: SquadStatusInput,
  options: { now?: Date } = {},
): ParseResult<NormalisedSquadStatusInput> {
  const allowedDates = allowedLocalDates(options.now ?? new Date(), input.timezone);
  const placeName = normaliseText(input.placeName, 120);
  const meetupId = normaliseUuid(input.meetupId);
  if (
    !normaliseUuid(input.squadId) ||
    !allowedDates?.has(input.targetLocalDate) ||
    !isStatusType(input.statusType) ||
    !placeName
  ) {
    return { ok: false, error: "Choose a valid Squad status and place." };
  }
  if (input.meetupId && !meetupId) {
    return { ok: false, error: "Choose a valid linked meet-up." };
  }

  return {
    ok: true,
    value: {
      squadId: input.squadId,
      statusType: input.statusType,
      targetLocalDate: input.targetLocalDate,
      timezone: input.timezone,
      placeSlug: normaliseSlug(input.placeSlug),
      placeName,
      approximateLabel: normaliseText(input.approximateLabel, 80),
      meetupId,
    },
  };
}

export function statusExpiry(
  statusType: SquadStatusType,
  now: Date,
  timezone: string,
  options: {
    targetLocalDate?: string | null;
    meetupExpiresAt?: string | null;
  } = {},
): string | null {
  const local = localParts(now, timezone);
  if (!local) return null;
  const targetLocalDate = options.targetLocalDate ?? local.date;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(targetLocalDate)) return null;
  if (statusType === "rest_day") {
    return localDayEnd(targetLocalDate, timezone);
  }
  if (statusType === "meet_here" && options.meetupExpiresAt) {
    const meetupExpiry = new Date(options.meetupExpiresAt);
    if (Number.isFinite(meetupExpiry.getTime())) return meetupExpiry.toISOString();
  }

  const anchor = targetLocalDate === local.date
    ? now
    : zonedDateTimeToUtc(
        targetLocalDate,
        timezone,
        local.hour,
        local.minute,
        local.second,
      );
  if (!anchor) return null;

  const durationMinutes: Record<Exclude<SquadStatusType, "rest_day">, number> = {
    out_now: 240,
    at_lunch: 120,
    pub: 240,
    heading_home: 90,
    meet_here: 240,
    watching_conditions: 120,
  };
  return new Date(anchor.getTime() + durationMinutes[statusType] * 60_000).toISOString();
}

export function localDateId(now: Date, timezone: string): string {
  return localParts(now, timezone)?.date ?? "";
}

function allowedLocalDates(now: Date, timezone: string): Set<string> | null {
  const today = localDateId(now, timezone);
  if (!today) return null;
  return new Set([today, shiftDateId(today, 1)]);
}

function localParts(
  now: Date,
  timezone: string,
): { date: string; hour: number; minute: number; second: number } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? "";
    const date = `${value("year")}-${value("month")}-${value("day")}`;
    const hour = Number(value("hour"));
    const minute = Number(value("minute"));
    const second = Number(value("second"));
    return /^\d{4}-\d{2}-\d{2}$/.test(date) &&
      Number.isFinite(hour) &&
      Number.isFinite(minute) &&
      Number.isFinite(second)
      ? { date, hour, minute, second }
      : null;
  } catch {
    return null;
  }
}

function localDayEnd(dateId: string, timezone: string): string | null {
  const nextDate = shiftDateId(dateId, 1);
  const midnight = zonedDateTimeToUtc(nextDate, timezone);
  return midnight ? new Date(midnight.getTime() - 1).toISOString() : null;
}

function zonedDateTimeToUtc(
  dateId: string,
  timezone: string,
  hour = 0,
  minute = 0,
  second = 0,
): Date | null {
  const [year, month, day] = dateId.split("-").map(Number);
  if (
    !year ||
    !month ||
    !day ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }
  const localAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  if (new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10) !== dateId) {
    return null;
  }

  const offsets = new Set<number>();
  for (let sampleHour = -36; sampleHour <= 36; sampleHour += 6) {
    const offset = timezoneOffsetMs(
      new Date(localAsUtc + sampleHour * 60 * 60 * 1000),
      timezone,
    );
    if (offset !== null) offsets.add(offset);
  }
  if (offsets.size === 0) return null;

  const candidates: Array<{
    instant: Date;
    localValue: number;
  }> = [];

  for (const offset of offsets) {
    const instant = new Date(localAsUtc - offset);
    const local = localParts(instant, timezone);
    if (!local) return null;
    const [localYear, localMonth, localDay] = local.date.split("-").map(Number);
    candidates.push({
      instant,
      localValue: Date.UTC(
        localYear,
        localMonth - 1,
        localDay,
        local.hour,
        local.minute,
        local.second,
      ),
    });
  }

  const exact = candidates
    .filter((candidate) => candidate.localValue === localAsUtc)
    .sort((left, right) => left.instant.getTime() - right.instant.getTime());
  if (exact[0]) return exact[0].instant;

  // Compatible disambiguation moves a missing wall time forwards by the gap.
  // Exact matches above choose the earlier overlap, avoiding extra visibility.
  const afterGap = candidates
    .filter((candidate) => candidate.localValue > localAsUtc)
    .sort((left, right) =>
      left.localValue - right.localValue ||
      left.instant.getTime() - right.instant.getTime()
    );
  return afterGap[0]?.instant ?? null;
}

function timezoneOffsetMs(date: Date, timezone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const number = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? Number.NaN);
    const asUtc = Date.UTC(
      number("year"),
      number("month") - 1,
      number("day"),
      number("hour"),
      number("minute"),
      number("second"),
    );
    return Number.isFinite(asUtc)
      ? asUtc - Math.floor(date.getTime() / 1000) * 1000
      : null;
  } catch {
    return null;
  }
}

function shiftDateId(dateId: string, days: number): string {
  const [year, month, day] = dateId.split("-").map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function normaliseUuid(value: string | null): string | null {
  const clean = value?.trim() ?? "";
  return UUID_PATTERN.test(clean) ? clean : null;
}

function normaliseSlug(value: string | null): string | null {
  const clean = value?.trim().toLowerCase() ?? "";
  return SLUG_PATTERN.test(clean) ? clean : null;
}

function normaliseSport(value: string | null): string | null {
  const clean = value?.trim().toLowerCase() ?? "";
  return SPORT_PATTERN.test(clean) ? clean : null;
}

function normaliseTime(value: string | null): string | null {
  const clean = value?.trim() ?? "";
  return TIME_PATTERN.test(clean) ? clean : null;
}

function normaliseText(value: string | null, maxLength: number): string | null {
  const clean = value?.trim().replace(/\s+/g, " ") ?? "";
  return clean.length > 0 && clean.length <= maxLength ? clean : null;
}

function isCommitmentType(value: string): value is SquadCommitmentType {
  return SQUAD_COMMITMENT_TYPES.includes(value as SquadCommitmentType);
}

function isStatusType(value: string): value is SquadStatusType {
  return SQUAD_STATUS_TYPES.includes(value as SquadStatusType);
}

function isMeetupLocationType(value: string): value is SquadMeetupLocationType {
  return SQUAD_MEETUP_LOCATION_TYPES.includes(value as SquadMeetupLocationType);
}

function validLatitude(value: number | null): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -90 && value <= 90;
}

function validLongitude(value: number | null): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= -180 && value <= 180;
}
